import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// "Journeys" PDF off the transfers data.
//   Part 1: one summary table of every bus journey (counts, not names).
//   Part 2: one page per provider — a filtered summary subset, then the
//           per-bus passenger detail.
// Self-transfer / not-on-a-bus passengers aren't bus journeys, so they're
// excluded. NB: jsPDF's standard font is Latin-1 only, so use ASCII ">" for
// the route arrow (a unicode arrow renders as garbage).

const stopTime = (v) => {
  const s = String(v || "").trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return "";
};

const isAdultRow = (t) =>
  String(t?.personType || "").toUpperCase() === "ADULT" ||
  (t?.adultId && Number(t.adultId) !== 0);

const journeyTypeOf = (g) =>
  String(g?.journeyType ?? g?.meta?.journeyType ?? "").trim().toUpperCase();

const dirLabel = (g) => {
  const jt = journeyTypeOf(g);
  if (jt === "ARRIVALS") return "Arr";
  if (jt === "DEPARTURES") return "Dep";
  return "-";
};

const groupTime = (g) => {
  const times = (Array.isArray(g?.stops) ? g.stops : [])
    .map((s) => stopTime(s.plannedTime))
    .filter(Boolean)
    .sort();
  return times[0] || "";
};

const formatRoute = (g) => {
  const stops = (Array.isArray(g?.stops) ? g.stops : []).slice();
  if (!stops.length) return "";
  return stops
    .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
    .map((s) => {
      const loc = String(s.location || "").trim() || "?";
      const t = stopTime(s.plannedTime);
      return t ? `${loc} ${t}` : loc;
    })
    .join(" > ");
};

const busPax = (g) =>
  (g.studentIds?.length || 0) + (g.adultIds?.length || 0);

const statusOf = (g) =>
  g.status || (g.confirmedAt ? "Confirmed" : "") || "Planned";

const isInternalTransfer = (t) =>
  String(t?.direction || "").toUpperCase() === "INTERNAL" ||
  t?.internalTransfer === true;

const isSelfTransfer = (t) =>
  String(t?.flightId || "").trim().toUpperCase() === "SELFTRANSFER" ||
  t?.privatePickup === true;

// A real, known flight number (not blank / placeholder / self-transfer).
const hasRealFlight = (t) => {
  const f = String(t?.flightId || "").trim().toUpperCase();
  return f !== "" && !["NONE", "SELFTRANSFER", "PENDING"].includes(f);
};

// People we don't have complete flight + transport details for on this date.
// Internal transfers (campus-to-campus, no flight needed) and self-transfers
// (private pickup — complete) are excluded.
const buildMissingRows = ({
  sortedTransfers,
  transportGroups,
  missingFlights,
  internalTransfers,
  dateStr,
}) => {
  const rows = [];

  const onBus = new Set();
  (transportGroups || []).forEach((g) => {
    (g.studentIds || []).forEach((id) => onBus.add(`s:${id}`));
    (g.adultIds || []).forEach((id) => onBus.add(`a:${id}`));
  });
  const keyOf = (t) => (isAdultRow(t) ? `a:${t.adultId}` : `s:${t.studentId}`);
  const busNameFor = (t) => {
    const g = (transportGroups || []).find((grp) =>
      isAdultRow(t) ? grp.adultIds?.includes(t.adultId) : grp.studentIds?.includes(t.studentId)
    );
    return g?.groupName || "";
  };

  // One row per person+direction — the date's transfer feed already includes
  // no-transfer people, so without deduping they'd also come from the
  // missing-transfers feed and appear twice.
  const seen = new Set();

  // From the date's transfer records: incomplete arrangements.
  (sortedTransfers || []).forEach((t) => {
    if (isInternalTransfer(t) || isSelfTransfer(t)) return;
    const flight = hasRealFlight(t);
    const bussed = onBus.has(keyOf(t));
    if (flight && bussed) return; // complete

    const k = `${keyOf(t)}-${String(t.direction || "").toUpperCase()}`;
    if (seen.has(k)) return;
    seen.add(k);

    const time = (t.direction === "IN" ? t.arrivalTime : t.departureTime) || "";
    rows.push([
      t.campus || "",
      t.name || "",
      t.bookingGroupName || "",
      flight ? `${t.flightId} ${time}`.trim() : "NONE",
      bussed ? busNameFor(t) : "NONE",
    ]);
  });

  // From the missing-transfers feed: people with no transfer at all on this
  // date (skip ones covered by an internal transfer, or already listed above).
  const covered = new Set(
    (internalTransfers || []).map((t) => `${t.studentId}-${t.transferDate}`)
  );
  const addMissing = (m, dir, date) => {
    if (date !== dateStr) return;
    if (covered.has(`${m.studentId}-${date}`)) return;
    const k = `s:${m.studentId}-${dir}`;
    if (seen.has(k)) return;
    seen.add(k);
    rows.push([
      m.stayCampus || "",
      `${m.studentName || ""} ${m.studentSurname || ""}`.trim(),
      m.bookingGroupName || "",
      "NONE",
      "NONE",
    ]);
  };
  (missingFlights || []).forEach((m) => {
    if (m.missingInbound) addMissing(m, "IN", m.stayStartDate);
    if (m.missingOutbound) addMissing(m, "OUT", m.stayEndDate);
  });

  return rows.sort(
    (a, b) => (a[0] || "").localeCompare(b[0] || "") || (a[1] || "").localeCompare(b[1] || "")
  );
};

// The bus's passengers, matched by id (any direction — handles internal
// transfers), one row per person.
const busPassengers = (g, transfers) => {
  const ids = new Set([
    ...(g.studentIds || []).map((id) => `s:${id}`),
    ...(g.adultIds || []).map((id) => `a:${id}`),
  ]);
  const seen = new Set();
  return (transfers || [])
    .filter((t) => {
      const k = isAdultRow(t) ? `a:${t.adultId}` : `s:${t.studentId}`;
      if (!ids.has(k) || seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
};

const busFlights = (pax) => {
  const set = new Set();
  pax.forEach((t) => {
    const f = String(t.flightId || "").trim().toUpperCase();
    if (f && f !== "SELFTRANSFER") set.add(f);
  });
  return Array.from(set).join(", ");
};

const SUMMARY_HEAD = [
  ["Time", "Dir", "Provider", "Bus", "Pax", "Flights", "UM", "Status", "Route"],
];

const summaryRow = (g, transfers) => {
  const pax = busPassengers(g, transfers);
  return [
    groupTime(g) || "-",
    dirLabel(g),
    g.provider || "No provider",
    g.groupName || "(unnamed)",
    String(busPax(g)),
    busFlights(pax) || "-",
    String(pax.filter((t) => t.um).length),
    statusOf(g),
    formatRoute(g) || "-",
  ];
};

const PASSENGER_HEAD = [["Name", "Campus", "Flight", "Time", "UM"]];

const passengerRows = (pax) =>
  pax.map((t) => [
    t.name || "",
    t.campus || "",
    t.flightId || "",
    (t.direction === "IN" ? t.arrivalTime : t.departureTime) ||
      t.arrivalTime ||
      t.departureTime ||
      "",
    t.um ? "Yes" : "No",
  ]);

export function downloadTransfersJourneysPdf({
  sortedTransfers,
  transportGroups,
  selectedDate,
  missingFlights = [],
  internalTransfers = [],
}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = 20;

  const dateStr = selectedDate?.format
    ? selectedDate.format("ddd D MMM YYYY")
    : String(selectedDate || "");

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = 20;
    }
  };

  const heading = (text, size = 14, gap) => {
    ensureSpace(size + 4);
    doc.setFont(undefined, "bold");
    doc.setFontSize(size);
    doc.text(text, margin, y);
    y += gap ?? (size >= 14 ? 7 : 6);
    doc.setFont(undefined, "normal");
  };

  // Only real bus journeys (transport groups) with a journey type.
  const groups = (transportGroups || []).filter((g) =>
    ["ARRIVALS", "DEPARTURES"].includes(journeyTypeOf(g))
  );
  // Every table is ordered by journey time (blank times last).
  const timeKey = (g) => groupTime(g) || "99:99";
  const ordered = (list) =>
    list.slice().sort((a, b) => timeKey(a).localeCompare(timeKey(b)));

  const summaryTable = (list) => {
    autoTable(doc, {
      startY: y,
      head: SUMMARY_HEAD,
      body: ordered(list).map((g) => summaryRow(g, sortedTransfers)),
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
      headStyles: { fillColor: [48, 84, 150], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 10 },
        4: { cellWidth: 9, halign: "center" },
        6: { cellWidth: 8, halign: "center" },
        8: { cellWidth: 50 },
      },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 6;
  };

  const busDetail = (g) => {
    const pax = busPassengers(g, sortedTransfers);
    ensureSpace(20);
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text(
      `${groupTime(g) ? groupTime(g) + "  " : ""}${g.groupName || "(unnamed)"}  -  ${busFlights(pax) || "no flight"}  -  ${busPax(g)} pax`,
      margin,
      y
    );
    y += 5;
    const route = formatRoute(g);
    if (route) {
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      doc.setTextColor(110);
      doc.text(doc.splitTextToSize(`Route: ${route}`, pageWidth - margin * 2), margin, y);
      doc.setTextColor(0);
      y += 5;
    }
    autoTable(doc, {
      startY: y,
      head: PASSENGER_HEAD,
      body: pax.length ? passengerRows(pax) : [["- no passengers -", "", "", "", ""]],
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [48, 84, 150], textColor: 255 },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 6;
  };

  // Title
  doc.setFont(undefined, "bold");
  doc.setFontSize(16);
  doc.text("Airport Transfers", margin, y);
  y += 6;
  doc.setFont(undefined, "normal");
  doc.setFontSize(10);
  doc.setTextColor(110);
  doc.text(dateStr, margin, y);
  doc.setTextColor(0);
  y += 8;

  // ── Missing flight / transfer details — the chase list (shown first) ────
  const missingRows = buildMissingRows({
    sortedTransfers,
    transportGroups,
    missingFlights,
    internalTransfers,
    dateStr: selectedDate?.format?.("YYYY-MM-DD") || "",
  });
  ensureSpace(12);
  doc.setFont(undefined, "bold");
  doc.setFontSize(13);
  doc.setTextColor(170, 0, 0);
  doc.text(`Missing flight / transfer details (${missingRows.length})`, margin, y);
  doc.setTextColor(0);
  doc.setFont(undefined, "normal");
  y += 6;
  autoTable(doc, {
    startY: y,
    head: [["Campus", "Name", "Booking Group", "Flight?", "Transport?"]],
    body: missingRows.length
      ? missingRows
      : [["-", "Everyone has a flight + transport", "", "", ""]],
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 1.5, overflow: "linebreak" },
    headStyles: { fillColor: [170, 0, 0], textColor: 255 },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 8;

  if (!groups.length) {
    doc.setFontSize(11);
    doc.text("No transport-group journeys for this date.", margin, y);
    doc.save(`Airport_Transfers_${selectedDate?.format?.("YYYY-MM-DD") || "export"}.pdf`);
    return;
  }

  // ── All journeys: one summary table (own page) ─────────────────────────
  doc.addPage();
  y = 20;
  heading("All journeys");
  summaryTable(groups);

  // ── Part 2: one page per provider — summary subset + per-bus detail ─────
  const providers = Array.from(
    new Set(groups.map((g) => g.provider || "No provider"))
  ).sort((a, b) => a.localeCompare(b));

  providers.forEach((prov) => {
    const provGroups = ordered(
      groups.filter((g) => (g.provider || "No provider") === prov)
    );
    if (!provGroups.length) return;

    doc.addPage();
    y = 20;
    heading(prov, 13);
    summaryTable(provGroups);
    provGroups.forEach(busDetail);
  });

  doc.save(`Airport_Transfers_${selectedDate?.format?.("YYYY-MM-DD") || "export"}.pdf`);
}
